import axios from 'axios';

import { publicClientApplication as msalInstance } from '../index';
import { msalScopes } from '../modules/auth/containers/Auth/constants';

const LOGOUT_FLAG_KEY = 'app_logout_in_progress';
const AUTH_INVALIDATE_COUNT_KEY = 'app_auth_invalidate_count';
const AUTH_DEBUG_KEY = 'app_auth_debug';

const isLogoutInProgress = () => sessionStorage.getItem(LOGOUT_FLAG_KEY) === '1';
const setLogoutInProgress = () => sessionStorage.setItem(LOGOUT_FLAG_KEY, '1');

const authLog = (message: string, details?: unknown) => {
  const isDebugEnabled = process.env.NODE_ENV !== 'production' || localStorage.getItem(AUTH_DEBUG_KEY) === '1';

  if (!isDebugEnabled) {
    return;
  }

  // eslint-disable-next-line no-console
  console.log(`[AUTH][axios] ${message}`, details ?? '');
};

export const instance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  headers: {
    'Access-Control-Allow-Methods': 'GET, POST, PATCH, PUT, DELETE, OPTIONS',
  },
});

export const publicInstance = axios.create({
  headers: {
    'Access-Control-Allow-Methods': 'GET, POST, PATCH, PUT, DELETE, OPTIONS',
  },
});

const invalidateSession = async (reason: string, details?: unknown) => {
  if (isLogoutInProgress()) {
    authLog('Session invalidation skipped: logout already in progress', { reason, details });
    return;
  }

  authLog('Session invalidation started', { reason, details });
  setLogoutInProgress();

  const invalidateCount = Number(sessionStorage.getItem(AUTH_INVALIDATE_COUNT_KEY) || '0') + 1;
  sessionStorage.setItem(AUTH_INVALIDATE_COUNT_KEY, String(invalidateCount));

  authLog('Invalidation counter incremented', { invalidateCount });

  localStorage.removeItem('role');
  localStorage.removeItem('depTitle');
  localStorage.removeItem('branchId');
  localStorage.removeItem('/ordering');
  localStorage.removeItem('/security');

  // Remove all MSAL keys from localStorage so next login starts fresh
  const isAllowedMicrosoftLoginHost = (host: string): boolean =>
    host === 'login.windows.net' || host === 'login.microsoftonline.com';

  const shouldRemoveAuthCacheKey = (key: string): boolean => {
    if (key.startsWith('msal.')) {
      return true;
    }

    try {
      const url = new URL(key);
      return url.protocol === 'https:' && isAllowedMicrosoftLoginHost(url.hostname);
    } catch {
      return key === 'login.windows.net';
    }
  };

  Object.keys(localStorage)
    .filter(shouldRemoveAuthCacheKey)
    .forEach((key) => localStorage.removeItem(key));

  if (window.self !== window.top) {
    authLog('Skipping redirect: inside iframe');
    return;
  }

  if (invalidateCount >= 2) {
    authLog('Circuit breaker triggered, doing hard redirect to root');
    sessionStorage.removeItem(LOGOUT_FLAG_KEY);
    sessionStorage.removeItem(AUTH_INVALIDATE_COUNT_KEY);
    window.location.replace('/');
    return;
  }

  authLog('Calling logoutRedirect to cleanly re-authenticate');
  const currentAccount = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
  try {
    await msalInstance.logoutRedirect({
      account: currentAccount ?? undefined,
      onRedirectNavigate: () => true,
    });
  } catch (error: any) {
    if (error?.errorCode !== 'interaction_in_progress') {
      authLog('logoutRedirect failed, falling back to hard redirect', { error: error?.message });
      window.location.replace('/');
    }
  }
};

const getToken = async () => {
  if (isLogoutInProgress()) {
    authLog('Token request skipped: logout in progress');
    return null;
  }

  const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];

  if (!account) {
    authLog('Token request skipped: no active account');
    return null;
  }

  try {
    authLog('Trying acquireTokenSilent', { username: account.username });
    const token = await msalInstance.acquireTokenSilent({
      // @ts-ignore
      scopes: msalScopes,
      account,
    });

    if (token.account) {
      msalInstance.setActiveAccount(token.account);
    }

    authLog('acquireTokenSilent success', { username: token.account?.username });
    return token.accessToken;
  } catch (error: any) {
    const errorCode = error?.errorCode || error?.subError;

    authLog('acquireTokenSilent failed', {
      errorCode,
      message: error?.message,
    });

    if (
      errorCode === 'monitor_window_timeout' ||
      errorCode === 'interaction_required' ||
      errorCode === 'no_account_error'
    ) {
      await invalidateSession('token_silent_failed', {
        errorCode,
        message: error?.message,
      });
      return null;
    }

    return null;
  }
};

instance.interceptors.request.use(
  async (config) => {
    const token = await getToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;

    if (status === 401 || status === 403) {
      authLog('API auth error received', {
        status,
        url: error?.config?.url,
        method: error?.config?.method,
      });
      await invalidateSession('api_401_403', { status, url: error?.config?.url });
    }

    return Promise.reject(error);
  },
);
